import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { IonTextarea, useIonToast } from '@ionic/react';
import {
  guardarEncuestaPedido,
  IEncuestaPregunta,
  IEncuestaPedidoPendiente,
  IEncuestaRespuestaInput,
} from 'src/services/PedidoDeProducto/pedido.producto.services';

// Bordó institucional de la app (definido en theme/variables.css como --ion-color-primary).
const BORDO = '#910434';
const BORDO_SHADE = '#80042d';

interface Props {
  open: boolean;
  pedido: IEncuestaPedidoPendiente | null;
  preguntas: IEncuestaPregunta[];
  onCompleted: () => void;
  onCancel: () => void;
}

/**
 * Botón toggle Sí/No.
 * Se usa en lugar de MUI Radio/FormControlLabel porque la mezcla MUI + Ionic
 * desalinea verticalmente los radios (line-height distinto por el acento de "Sí").
 * Como <button> plano con flex centrado siempre quedan a la misma altura.
 */
const ToggleSiNo: React.FC<{
  valor: 0 | 1 | undefined;
  onChange: (v: 0 | 1) => void;
}> = ({ valor, onChange }) => {
  const baseStyle: React.CSSProperties = {
    flex: 1,
    height: 40,
    padding: '0 12px',
    border: `1.5px solid ${BORDO}`,
    background: '#fff',
    color: BORDO,
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
  };
  const selectedStyle: React.CSSProperties = {
    background: BORDO,
    color: '#fff',
  };

  return (
    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
      <button
        type="button"
        onClick={() => onChange(1)}
        style={{ ...baseStyle, ...(valor === 1 ? selectedStyle : {}) }}
      >
        Sí
      </button>
      <button
        type="button"
        onClick={() => onChange(0)}
        style={{ ...baseStyle, ...(valor === 0 ? selectedStyle : {}) }}
      >
        No
      </button>
    </div>
  );
};

const EncuestaPedidoProducto: React.FC<Props> = ({ open, pedido, preguntas, onCompleted, onCancel }) => {
  const [respuestas, setRespuestas] = useState<Record<number, 0 | 1 | undefined>>({});
  const [comentario, setComentario] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [present] = useIonToast();

  const tieneNo = useMemo(
    () => Object.values(respuestas).some((v) => v === 0),
    [respuestas]
  );

  const preguntasObligatorias = useMemo(
    () => preguntas.filter((p) => p.obligatoria),
    [preguntas]
  );

  const todasObligatoriasRespondidas = useMemo(
    () => preguntasObligatorias.every((p) => respuestas[p.id] !== undefined),
    [preguntasObligatorias, respuestas]
  );

  const comentarioValido = !tieneNo || comentario.trim().length > 0;
  const puedeGuardar = todasObligatoriasRespondidas && comentarioValido && !submitting;

  const handleSetRespuesta = (preguntaId: number, valor: 0 | 1) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: valor }));
  };

  const handleGuardar = async () => {
    if (!pedido) return;
    if (!todasObligatoriasRespondidas) {
      present('Debe responder todas las preguntas', 3000);
      return;
    }
    if (!comentarioValido) {
      present('El comentario es obligatorio si alguna respuesta es No', 3000);
      return;
    }

    setSubmitting(true);
    try {
      const payload: IEncuestaRespuestaInput[] = preguntas
        .filter((p) => respuestas[p.id] !== undefined)
        .map((p) => ({ pregunta_id: p.id, respuesta: respuestas[p.id] as 0 | 1 }));

      const resp = await guardarEncuestaPedido(
        pedido.id_pc,
        tieneNo ? comentario.trim() : (comentario.trim() || null),
        payload
      );

      if (resp.error) {
        present(resp.error, 4000);
        return;
      }

      present('Encuesta guardada correctamente', 2500);
      setRespuestas({});
      setComentario('');
      onCompleted();
    } catch (e) {
      console.error('Error al guardar encuesta:', e);
      present('Ocurrió un error al guardar la encuesta', 3000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onCancel();
      }}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
    >
      <DialogTitle
        sx={{
          backgroundColor: BORDO,
          color: '#fff',
          py: 1.5,
          fontWeight: 600,
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}
      >
        Encuesta del pedido anterior
      </DialogTitle>
      <DialogContent dividers>
        {pedido && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Pedido <b>#{pedido.id_pc}</b> &middot; PS <b>{pedido.ps_objeto}</b>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fecha de envío: {pedido.fecha}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Para iniciar un nuevo pedido en este punto de servicio debe completar la encuesta del pedido anterior.
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {preguntas.map((p) => (
          <Box key={p.id} mb={2}>
            <Typography variant="body1" gutterBottom>
              {p.orden}. {p.texto}
              {p.obligatoria ? <span style={{ color: BORDO }}> *</span> : null}
            </Typography>
            <ToggleSiNo
              valor={respuestas[p.id]}
              onChange={(v) => handleSetRespuesta(p.id, v)}
            />
          </Box>
        ))}

        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            Comentario{tieneNo ? <span style={{ color: BORDO }}> * (obligatorio)</span> : ' (opcional)'}
          </Typography>
          {/* IonTextarea en vez de MUI TextField: el TextField sufre interferencia
              del CSS reset de Ionic y el texto se renderiza fuera del input. */}
          <div
            style={{
              border: `1px solid ${tieneNo && comentario.trim().length === 0 ? BORDO : '#c4c4c4'}`,
              borderRadius: 6,
              padding: '4px 8px',
              background: '#fff',
            }}
          >
            <IonTextarea
              autoGrow
              rows={3}
              value={comentario}
              placeholder={
                tieneNo
                  ? 'Detalle el motivo de las respuestas "No"'
                  : 'Comentarios u observaciones adicionales'
              }
              onIonChange={(e) => setComentario(e.detail.value || '')}
            />
          </div>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={onCancel}
          disabled={submitting}
          variant="outlined"
          sx={{
            color: BORDO,
            borderColor: BORDO,
            minWidth: 0,
            '&:hover': { borderColor: BORDO_SHADE, backgroundColor: 'rgba(145,4,52,0.04)' },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          disabled={!puedeGuardar}
          sx={{
            backgroundColor: BORDO,
            minWidth: 0,
            '&:hover': { backgroundColor: BORDO_SHADE },
            '&.Mui-disabled': { backgroundColor: 'rgba(145,4,52,0.4)', color: '#fff' },
          }}
        >
          {submitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EncuestaPedidoProducto;
